import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";

import { MatRipple } from "@angular/material/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

export interface AccountSearchRequest<T extends string = string> {
    query: string;
    filters: Record<T, boolean>;
}

export interface AccountSearchFilter<T extends string = string> {
    display: string;
    key: T;
}

@Component({
    selector: "account-search",
    templateUrl: "./account-search.component.html",
    styleUrls: ["./account-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSearchComponent implements OnChanges {
    @ViewChild(MatRipple) suffixRipple: MatRipple;

    @Output() search = new EventEmitter<AccountSearchRequest>();

    @Input()
    filters: AccountSearchFilter[] = [];

    formData: AccountSearchRequest = { query: "", filters: {} };
    isSettingsVisible = false;
    private isShowingRipple = false;

    constructor(private elementRef: ElementRef<Element>, iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
        iconRegistry.addSvgIcon("filter", sanitizer.bypassSecurityTrustResourceUrl("assets/images/filter.svg"));
        iconRegistry.addSvgIcon("search", sanitizer.bypassSecurityTrustResourceUrl("assets/images/search.svg"));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.filters) {
            // Default any new filters to true
            const filterKeys = this.filters.map(f => f.key);
            filterKeys.forEach(key => {
                this.formData.filters[key] = this.formData.filters[key] ?? true;
            });

            // Remove deleted filters
            Object.keys(this.formData.filters)
                .filter(filter => !filterKeys.includes(filter))
                .forEach(key => delete this.formData.filters[key]);
        }
    }

    setSettingsVisibility(isVisible: boolean) {
        this.isSettingsVisible = isVisible;
    }

    /**
     * Set ripple visibility
     *
     * @param isVisible
     * @param ignoreIfSet If false and the ripple is already visible, the ripple is cleared and recreated. Defaults to false.
     */
    setRipple(isVisible: boolean, ignoreIfSet = false) {
        // If we're already in the desired state, do nothing
        if (isVisible === this.isShowingRipple && ignoreIfSet) {
            return;
        }

        // Set state
        if (this.suffixRipple) {
            if (isVisible) {
                if (this.isShowingRipple) {
                    this.suffixRipple.fadeOutAll();
                }
                this.suffixRipple.launch({ persistent: true, radius: 16, centered: true });
            } else {
                this.suffixRipple.fadeOutAll();
            }
        }

        this.isShowingRipple = isVisible;
    }

    onSettingsToggle($event: Event, isKeyboardEvent: boolean = true) {
        /**
         * The ripple will be visible if either...
         *   (1) the trigger element (the filter icon) has focus
         *   (2) the settings menu is open
         *
         * On keyboard events...
         *    When the user tabs to the trigger, the ripple should appear
         *        This should always trigger an "opening" animation, even if the menu is already open due to case (2)
         *    When the user presses enter / spacebar on the trigger, we open / close the settings section, BUT the ripple should always remain visible (because the trigger still has keyboard focus)
         *
         * On mouse events...
         *    When the user clicks the trigger, we pretend the ripple no longer has focus, so that the ripple visibility is determined by case (2)
         *    If the ripple is already visible, the mouse click should never trigger another opening animation
         */

        this.setSettingsVisibility(!this.isSettingsVisible);

        // Mouse event scenario
        if (!isKeyboardEvent) {
            // By passing in ignoreIfSet=True, we prevent unnessary opening animations
            this.setRipple(this.isSettingsVisible, true);
        }

        // Don't auto-focus input after user clicks the settings icon
        $event.stopImmediatePropagation();
    }

    onClear() {
        // Uncheck all filters
        Object.keys(this.formData.filters).forEach(key => (this.formData.filters[key] = false));
    }

    onSubmit() {
        this.search.emit(this.formData);
    }

    @HostListener("window:click", ["$event"])
    onClick($event: MouseEvent) {
        // Collapse settings pane if user clicked outside the pane / input
        const targetIsThis = this.elementRef.nativeElement.contains($event.target as Node);
        if (!targetIsThis) {
            this.setSettingsVisibility(false);
            this.setRipple(false);
        }
    }

    get isSettingsChanged(): boolean {
        // Filters have been edited if some boxes are unticked
        return Object.values(this.formData.filters).some(f => !f);
    }

    trackFilterBy(filter: AccountSearchFilter) {
        return filter.key;
    }
}
